import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { PageHeader } from '../../components/PageHeader';
import { Pill, type PillTone } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { listBranches } from '../../data/fetchers';
import { fmtDate } from '../../lib/format';
import { listTransfers, type TransferView } from '../../lib/transfers';
import { useBranchScope } from '../../lib/useBranchScope';

const STATUS_TH: Record<TransferView['status'], string> = {
  draft: 'ร่าง',
  sent: 'ส่งแล้ว',
  received: 'รับแล้ว',
  cancelled: 'ยกเลิก',
};
const STATUS_TONE: Record<TransferView['status'], PillTone> = {
  draft: 'neutral',
  sent: 'primary',
  received: 'ok',
  cancelled: 'danger',
};
const TABS: { key: TransferView['status'] | 'all'; label: string }[] = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'sent', label: 'ส่งแล้ว' },
  { key: 'received', label: 'รับแล้ว' },
  { key: 'draft', label: 'ร่าง' },
  { key: 'cancelled', label: 'ยกเลิก' },
];

interface TrRow {
  id: string;
  referenceNo: string;
  from: string;
  to: string;
  date: string;
  status: TransferView['status'];
  lines: number;
}

export function TransfersView() {
  const navigate = useNavigate();
  const { scope, ready } = useBranchScope();
  const [tab, setTab] = useState<TransferView['status'] | 'all'>('all');
  const { data: transfers = [] } = useQuery({
    queryKey: ['transfers', scope.branchId, scope.allowedBranchIds],
    queryFn: () => listTransfers(scope),
    enabled: ready,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const shortOf = (id: string) =>
    branches.find((b) => b.id === id)?.short ?? id;

  const rows = useMemo<TrRow[]>(
    () =>
      transfers
        .filter((t) => tab === 'all' || t.status === tab)
        .map((t) => ({
          id: t.id,
          referenceNo: t.referenceNo,
          from: shortOf(t.fromBranchId),
          to: shortOf(t.toBranchId),
          date: fmtDate(t.createdAt),
          status: t.status,
          lines: t.lines.length,
        })),
    [transfers, branches, tab],
  );

  const columns: ColumnDef<TrRow, unknown>[] = [
    { accessorKey: 'referenceNo', header: 'เลขที่' },
    { accessorKey: 'from', header: 'จาก' },
    { accessorKey: 'to', header: 'ไป' },
    { accessorKey: 'date', header: 'วันที่' },
    {
      accessorKey: 'lines',
      header: 'รายการ',
      cell: (c) => <span className="num">{c.getValue<number>()}</span>,
    },
    {
      accessorKey: 'status',
      header: 'สถานะ',
      cell: (c) => {
        const s = c.getValue<TransferView['status']>();
        return <Pill tone={STATUS_TONE[s]}>{STATUS_TH[s]}</Pill>;
      },
    },
    {
      id: 'go',
      header: '',
      cell: (c) => (
        <button
          className="text-primary text-sm-label"
          onClick={() => navigate(`/transfers/${c.row.original.id}`)}
        >
          ดู
        </button>
      ),
    },
  ];

  return (
    <div className="bo-fade">
      <PageHeader
        title="โอนย้ายระหว่างสาขา"
        subtitle={`${transfers.length} รายการ`}
        actions={
          <Btn kind="primary" onClick={() => navigate('/transfers/new')}>
            + สร้างใบโอน
          </Btn>
        }
      />
      <div className="flex gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`h-8 px-3 rounded-btn text-sm-label ${tab === t.key ? 'bg-primary text-white font-bold' : 'bg-surface border border-line text-ink-2'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <DataTable columns={columns} data={rows} />
    </div>
  );
}
